import api from "@/utils/api";
import { BALANCE_FINAL_TYPE, buildBalanceSaveBody } from "@/utils/balanceSchedule";

export const state = {
  _balanceSchedule: null,
  _error: null,
};

export const mutations = {
  setBalanceSchedule(state, data) {
    state._balanceSchedule = data;
  },
  setError(state, error) {
    state._error = error;
  },
  resetBuyReport(state) {
    state._balanceSchedule = null;
    state._error = null;
  },
};

export const actions = {
  async acSaveBalanceSchedule({ commit, rootState }, params) {
    try {
      // 날짜 형식 변환: "YYYY-MM-DD" → "YYYYMMDD"
      const balanceDate = params.balance_date.replace(/-/g, "");

      // document URL 인코딩 (특수문자 +, =, / 등 처리)
      const encodedDocument = encodeURIComponent(params.document);
      const url = `/api/v1/report/${encodedDocument}/balance-date`;
      // NZSC-4010: 최종값 종류 동봉(둘 다 보관 모델). 폴백 입력 화면·업그레이드 경로도 같은 액션을 쓴다
      // 구 BE는 forbidNonWhitelisted로 400 — api-public NZSC-4010과 같은 릴리스(4.953) 필수
      const requestBody = {
        balance_date: balanceDate,
        balance_final_type: BALANCE_FINAL_TYPE.DATE,
      };

      const res = await api.patch(url, requestBody);

      if (res.data.code === 0) {
        commit("setBalanceSchedule", res.data.data);
        return { success: true };
      }

      return { success: false, message: "잔금일 저장에 실패했습니다." };
    } catch (error) {
      console.error("[buyReport] 잔금일 저장 실패:", error);
      commit("setError", error);

      // API 에러 응답 처리
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || "잔금일 저장 중 오류가 발생했습니다.",
        };
      }

      return { success: false, message: "잔금일 저장 중 오류가 발생했습니다." };
    }
  },

  /**
   * NZSC-3814: 초·중·하순(시기) 잔금일 저장.
   * balance_date와 동시 전송 금지(400 7507) — 시기 파라미터만 보낸다(NZSC-4010: + balance_final_type=PERIOD).
   * 구 BE는 forbidNonWhitelisted로 400 — api-public NZSC-4010과 같은 릴리스(4.953) 필수
   * @param {string} params.document 리포트 document
   * @param {string} params.ym "YYYYMM"
   * @param {string} params.part "EARLY" | "MID" | "LATE"
   */
  async acSaveBalancePeriod({ commit }, params) {
    try {
      const encodedDocument = encodeURIComponent(params.document);
      const url = `/api/v1/report/${encodedDocument}/balance-date`;
      const requestBody = {
        balance_period_ym: params.ym,
        balance_period_part: params.part,
        balance_final_type: BALANCE_FINAL_TYPE.PERIOD,
      };

      const res = await api.patch(url, requestBody);

      if (res.data.code === 0) {
        commit("setBalanceSchedule", res.data.data);
        return { success: true };
      }

      return { success: false, message: "잔금일 저장에 실패했습니다." };
    } catch (error) {
      console.error("[buyReport] 잔금일(시기) 저장 실패:", error);
      commit("setError", error);

      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || "잔금일 저장 중 오류가 발생했습니다.",
        };
      }

      return { success: false, message: "잔금일 저장 중 오류가 발생했습니다." };
    }
  },

  /**
   * NZSC-4010: 확인 화면에서 잔금일 수정 저장 — 최종값 종류(balance_final_type) 동봉.
   * 정확한 날짜와 초·중·하순은 서버가 둘 다 보관하므로, 새로 고른 종류의 값만 보내고
   * 반대 종류의 이전 값은 지우지 않는다.
   * @param {string} params.document 리포트 document
   * @param {{type:'date',date:string}|{type:'period',ym:string,part:string}} params.selection
   */
  async acSaveBalanceFinal({ commit }, params) {
    try {
      const encodedDocument = encodeURIComponent(params.document);
      const url = `/api/v1/report/${encodedDocument}/balance-date`;
      const res = await api.patch(url, buildBalanceSaveBody(params.selection));

      if (res.data.code === 0) {
        commit("setBalanceSchedule", res.data.data);
        return { success: true };
      }
      return { success: false, message: "잔금일 저장에 실패했습니다." };
    } catch (error) {
      console.error("[buyReport] 잔금일(최종값) 저장 실패:", error);
      commit("setError", error);
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || "잔금일 저장 중 오류가 발생했습니다.",
        };
      }
      return { success: false, message: "잔금일 저장 중 오류가 발생했습니다." };
    }
  },

  /**
   * NZSC-4010: 열람 전 잔금일 확인 완료 — balance_confirm_date 세팅.
   * 프리미엄은 이 시점부터 초·중·하순 요청 알림톡 대상에 들어간다(R15).
   */
  async acConfirmBalance({ commit }, params) {
    try {
      const encodedDocument = encodeURIComponent(params.document);
      const url = `/api/v1/report/${encodedDocument}/balance-confirm`;
      const res = await api.post(url, {});
      if (res.data.code === 0) {
        const d = res.data.data || {};
        commit(
          "reportStore/setReportBalanceConfirmed",
          {
            reportSeq: d.reportSeq,
            balanceConfirmDate: d.balanceConfirmDate,
            balanceFinalType: d.balanceFinalType,
          },
          { root: true },
        );
        return { success: true, data: d };
      }
      return { success: false, message: "잔금일 확인 처리에 실패했습니다." };
    } catch (error) {
      console.error("[buyReport] 잔금일 확인 실패:", error);
      commit("setError", error);
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || "잔금일 확인 중 오류가 발생했습니다.",
        };
      }
      return { success: false, message: "잔금일 확인 중 오류가 발생했습니다." };
    }
  },

  async acGetBalanceSchedule({ commit }, orderSeq) {
    try {
      // TODO: 백엔드 API 구현 후 아래 주석 해제
      /*
      const url = `/api/v1/buy-report/balance-schedule/${orderSeq}`;
      const res = await api.get(url);

      if (res.data.code === 0) {
        commit("setBalanceSchedule", res.data.data);
        return res.data.data;
      }
      return null;
      */

      // 임시 코드
      console.log("[buyReport] 전금일 조회 요청:", orderSeq);
      return state._balanceSchedule;
    } catch (error) {
      console.error("[buyReport] 전금일 조회 실패:", error);
      commit("setError", error);
      return null;
    }
  },
};

export const getters = {
  balanceSchedule: state => state._balanceSchedule,
  buyReportError: state => state._error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
